import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { ChevronDown, Calendar, Sparkles, Save, Share2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { apiClient, generateShareLink } from "../api/api";
import { useFormAction } from "../context/FormActionContext";
import ShareModal from './ShareModal';
import "./Header.css";

const Header = ({ isPublic = false, publicShopName = "", dateRange: publicDateRange, setDateRange: setPublicDateRange }) => {
  const location = useLocation();
  const isSettingsPage = location.pathname.startsWith("/settings");
  
  const { user, selectedShopId, setSelectedShopId, shops, dateRange: authDateRange, setDateRange: setAuthDateRange } = useAuth();
  const dateRange = isPublic ? publicDateRange : authDateRange;
  const setDateRange = isPublic ? setPublicDateRange : setAuthDateRange;

  const { triggerSave, isSaving } = useFormAction();
  
  const [showShopDropdown, setShowShopDropdown] = useState(false);
  const [selectedShopName, setSelectedShopName] = useState("Select Shop");

  const [showDateDropdown, setShowDateDropdown] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  const dateOptions = ["Today", "Yesterday", "Last 7 Days", "Last 28-30 Days"];

  useEffect(() => {
    if (shops && shops.length > 0) {
      if (selectedShopId) {
        const currentShop = shops.find(s => s._id === selectedShopId);
        if (currentShop) {
            setSelectedShopName(currentShop.shopName || currentShop.brand_name || `Shop …${String(currentShop._id).slice(-4)}`);
        }
      } else {
         // Default to first
         setSelectedShopId(shops[0]._id);
         setSelectedShopName(shops[0].shopName || shops[0].brand_name || `Shop …${String(shops[0]._id).slice(-4)}`);
      }
    } else {
        setSelectedShopName("Select Shop");
    }
  }, [shops, selectedShopId, setSelectedShopId]);

  return (
    <>
    <header className="app-header">
      <div className="header-container">
      {/* Left: Brand Selector */}
      <div className="header-left">
        {isPublic && (
          <div className="public-logo-container">
            <img src="/shopOS_Brand_Logo.png" alt="shopOS Logo" className="header-logo-img" />
          </div>
        )}
        <div style={{ position: "relative" }}>
          {!isPublic && (
          <div 
            className="brand-dropdown"
            onClick={() => setShowShopDropdown(!showShopDropdown)}
          >
            <span>{selectedShopName}</span>
            <ChevronDown className="icon-small" size={16} />
          </div>
        )}
          
          {showShopDropdown && shops.length > 0 && (
            <div className="header-dropdown-menu brand-dropdown-menu">
              {shops.map((shop) => (
                <div
                  key={shop._id}
                  className={`header-dropdown-item ${selectedShopId === shop._id ? 'selected' : ''}`}
                  onClick={() => {
                    setSelectedShopId(shop._id);
                    setSelectedShopName(shop.shopName || "Unknown Shop");
                    setShowShopDropdown(false);
                  }}
                >
                  {shop.shopName || "Unknown Shop"}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Center: Public Shop Name */}
      {isPublic && (
        <div className="header-center">
          <div className="public-shop-badge">
            <span className="status-dot"></span>
            <span className="public-shop-name">{publicShopName}</span>
          </div>
        </div>
      )}

      {/* Right: Dynamic Actions */}
      <div className="header-right">
        {isSettingsPage ? (
          <button className="header-action-button">
            <Save className="icon-small" size={16} />
            <span>Save Changes</span>
          </button>
        ) : (
          <>
            <div className="date-button-container">
                <div 
                    className="date-button"
                    onClick={() => setShowDateDropdown(!showDateDropdown)}
                >
                    <Calendar className="icon-small" size={16} />
                    <span>{dateRange}</span>
                    <ChevronDown size={14} style={{ marginLeft: "4px" }} />
                </div>
                {showDateDropdown && (
                    <div className="header-dropdown-menu">
                        {dateOptions.map((option) => (
                            <div
                                key={option}
                                className={`header-dropdown-item ${dateRange === option ? 'selected' : ''}`}
                                onClick={() => {
                                    setDateRange(option);
                                    setShowDateDropdown(false);
                                }}
                            >
                                {option}
                            </div>
                        ))}
                    </div>
                )}
            </div>
            {!isPublic && (
              <>
                <button 
                  className="share-dashboard-btn"
                  onClick={() => setIsShareModalOpen(true)}
                  title="Share Dashboard"
                >
                  <Share2 size={16} className="icon-small" />
                  <span>Share Dashboard</span>
                </button>
                <button className="header-action-button">
                  <Sparkles className="icon-small" size={16} />
                  <span>ShopAI</span>
                </button>
              </>
            )}
          </>
        )}
      </div>

      <ShareModal 
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        shopId={selectedShopId}
        generateShareLink={generateShareLink}
      />
      </div>
    </header>
    {/* Sync Notification Bar */}
    {(() => {
        const currentShop = shops.find(s => s._id === selectedShopId);
        const isSyncing = currentShop?.activationStatus?.toLowerCase() === "syncing";
        if (!isSyncing) return null;

        return (
            <div className="sync-notification-bar">
                <span>Syncing Shop Data</span>
                <div className="dot-flashing" style={{ backgroundColor: '#ffffff' }}></div>
            </div>
        );
    })()}
    </>
  );
};

export default Header;
